/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useEffect, useRef } from "react";
import { useTabNavigationContext } from "@/context/tabNavigation";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const THRESHOLD = 64; // px of horizontal travel before a swipe counts
const MAX_OFF_AXIS = 0.6; // |dy| must stay under this fraction of |dx|
const MAX_DURATION = 600; // ms — a slow drag is a scroll, not a flick

/** Does any ancestor up to `root` scroll horizontally? Swipes that start
 *  inside charts, wide tables, or anything marked [data-no-swipe] must not
 *  hijack the gesture into a tab change. */
function startsInHorizontalScroller(target: EventTarget | null, root: HTMLElement): boolean {
  let el = target as HTMLElement | null;
  while (el && el !== root) {
    if (el.dataset && el.dataset.noSwipe !== undefined) return true;
    if (el.scrollWidth > el.clientWidth + 4) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === "auto" || ox === "scroll") return true;
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Horizontal flicks on `ref` move to the previous/next primary tab. Mobile
 * only; no-op on ≥ sm and inside horizontal scrollers.
 */
export function useSwipeNavigation(ref: React.RefObject<HTMLElement | null>): void {
  const { tabs, activeTab, onTabChange } = useTabNavigationContext();
  const enabled = useMediaQuery("(max-width: 639px)");

  // Keep latest nav state in a ref so the touch listeners stay bound once but
  // always read current values (onTabChange is a fresh closure each render).
  const nav = useRef({ tabs, activeTab, onTabChange });
  useEffect(() => {
    nav.current = { tabs, activeTab, onTabChange };
  });

  useEffect(() => {
    const node = ref.current;
    if (!node || !enabled) return;

    let x0 = 0;
    let y0 = 0;
    let t0 = 0;
    let skip = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        skip = true;
        return;
      }
      const tch = e.touches[0];
      x0 = tch.clientX;
      y0 = tch.clientY;
      t0 = Date.now();
      skip = startsInHorizontalScroller(e.target, node);
    };

    const onEnd = (e: TouchEvent) => {
      if (skip) return;
      const tch = e.changedTouches[0];
      if (!tch) return;
      const dx = tch.clientX - x0;
      const dy = tch.clientY - y0;
      const dt = Date.now() - t0;
      if (dt > MAX_DURATION) return;
      if (Math.abs(dx) < THRESHOLD) return;
      if (Math.abs(dy) > Math.abs(dx) * MAX_OFF_AXIS) return;

      const { tabs: ts, activeTab: at, onTabChange: go } = nav.current;
      const i = ts.findIndex((t) => t.id === at);
      if (i < 0) return;
      const next = dx < 0 ? i + 1 : i - 1; // swipe left → next tab
      if (next < 0 || next >= ts.length) return;
      go(ts[next].id);
    };

    node.addEventListener("touchstart", onStart, { passive: true });
    node.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      node.removeEventListener("touchstart", onStart);
      node.removeEventListener("touchend", onEnd);
    };
  }, [ref, enabled]);
}
